import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

import { env } from '@/shared/config/env'

import type { ApiEnvelope, FieldErrors } from './types'

type AccessTokenResolver = () => string | null
type SessionRefreshHandler = () => Promise<string | null>
type UnauthorizedHandler = () => void

let resolveAccessToken: AccessTokenResolver = () => null
let refreshSession: SessionRefreshHandler = async () => null
let handleUnauthorized: UnauthorizedHandler = () => undefined

export class ApiError extends Error {
  status: number
  code?: string
  fields?: FieldErrors
  cause?: unknown

  constructor(
    message: string,
    options: { status?: number; code?: string; fields?: FieldErrors; cause?: unknown } = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status ?? 0
    this.code = options.code
    this.fields = options.fields
    this.cause = options.cause
  }
}

export function setAccessTokenResolver(resolver: AccessTokenResolver) {
  resolveAccessToken = resolver
}

export function setSessionRefreshHandler(handler: SessionRefreshHandler) {
  refreshSession = handler
}

export function setUnauthorizedHandler(handler: UnauthorizedHandler) {
  handleUnauthorized = handler
}

const client = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  withCredentials: true,
  headers: { Accept: 'application/json' },
})

client.interceptors.request.use((config) => {
  const token = resolveAccessToken()
  if (token) config.headers.set('Authorization', 'Bearer ' + token)
  config.headers.set('X-Request-ID', crypto.randomUUID())
  return config
})

type RetryableRequestConfig = InternalAxiosRequestConfig & { _sessionRetry?: boolean }

function canRefresh(config?: InternalAxiosRequestConfig): config is RetryableRequestConfig {
  if (!config || (config as RetryableRequestConfig)._sessionRetry) return false
  const path = config.url ?? ''
  return !['/auth/login', '/auth/refresh', '/auth/logout'].some((endpoint) =>
    path.endsWith(endpoint),
  )
}

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config
    if (error.response?.status === 401 && canRefresh(config)) {
      config._sessionRetry = true
      let token: string | null = null
      try {
        token = await refreshSession()
      } catch {
        // The original unauthorized response remains the public API error.
      }
      if (token) {
        config.headers.set('Authorization', 'Bearer ' + token)
        return client.request(config)
      }
      handleUnauthorized()
    } else if (
      error.response?.status === 401 &&
      (config as RetryableRequestConfig | undefined)?._sessionRetry
    ) {
      handleUnauthorized()
    }

    const payload = error.response?.data as
      { detail?: string; message?: string; code?: string; errors?: FieldErrors } | undefined

    throw new ApiError(
      payload?.detail ?? payload?.message ?? error.message ?? '请求失败，请稍后重试',
      {
        status: error.response?.status,
        code: payload?.code,
        fields: payload?.errors,
        cause: error,
      },
    )
  },
)

function isEnvelope<T>(value: unknown): value is ApiEnvelope<T> {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return typeof record.success === 'boolean' && 'data' in record
}

export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await client.request<T | ApiEnvelope<T>>(config)
  return isEnvelope<T>(response.data) ? response.data.data : response.data
}

export const http = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'GET', url }),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'POST', url, data }),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PUT', url, data }),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PATCH', url, data }),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'DELETE', url }),
}
